import { useMemo } from 'react'
import { Button } from 'react-aria-components'
import { useAsyncFn } from 'react-use'
import { match } from 'ts-pattern'
import * as v from 'valibot'
import MingcuteCheckFill from '~icons/mingcute/check-fill'
import RiVideoAiLine from '~icons/ri/video-ai-line'
import ArtworkFrame from 'src/components/Artwork/ArtworkFrame'
import { UITooltipResponsive } from 'src/components/HelpIcon'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import UserAvatar from 'src/components/UserAvatar'
import AvatarFrame from 'src/components/UserAvatar/AvatarFrame'
import UserBadgeToken from 'src/components/UserBadgeToken'
import { useUserProfileContext } from 'src/components/UserProfile/UserProfile.context'
import { useLocalizedStringPairGetter } from 'src/i18n/localizedContent'
import { refetchGetMyInfo } from 'src/services/apollo/auth'
import type {
  Decoration,
  UserDecorationRelation,
} from 'src/services/apollo/generated/graphql'
import { DecorationType } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { useToggleActiveDecoration } from '~/services/orpc/decoration'
import { getDecorationDataSchema } from '../schema/decoration'
import { decorationRemarkEditable } from '../utils/decorationRemark'
import { LiveBannerItem as LiveBannerItemDisplay } from './LiveBannerItemDisplay'
import { EditUserDecorationRemarkButton } from './UserDecorationRemark'

function convertObtainedFromToI18nKey(obtainedFrom?: string) {
  if (!obtainedFrom) return undefined

  // Special case
  if (obtainedFrom === 'shop_purchase') return 'shop_purchase'

  // Return the first part of the string until it meets _ or :
  const match = obtainedFrom.match(/^([^_:]+)/)
  return match ? match[1] : obtainedFrom
}

export function DecorationItemGroupInUserProfile(props: {
  decorationRelation: UserDecorationRelation
}) {
  const { isMyProfile } = useUserProfileContext()
  const { decoration, obtainedAt, obtainedFrom } = props.decorationRelation
  const getText = useLocalizedStringPairGetter()

  const decorationDataOutput = useMemo(() => {
    const schema = getDecorationDataSchema(decoration.type)
    if (schema) {
      const decorationData = v.safeParse(schema, decoration.data)
      if (decorationData.success) {
        return decorationData.output
      }
    }
  }, [decoration])

  const decorationName = useMemo(() => {
    if (decorationDataOutput?.name) {
      return getText(decorationDataOutput.name)
    }
    return '-'
  }, [decorationDataOutput?.name])

  const isArtworkFrame = decoration.type === DecorationType.ArtworkFrame
  const enableRemarkEdit = decorationRemarkEditable(decoration)

  const obtainedFromKey = convertObtainedFromToI18nKey(obtainedFrom)

  return (
    <div className="flex flex-col items-center gap-1">
      <UITooltipResponsive
        trigger={open => (
          // Special style for artwork frame to increase visibility
          <Button onPress={open} className={cx(isArtworkFrame && 'w-full')}>
            {match(decoration.type)
              .with(DecorationType.Badge, () => (
                <UserBadgeToken decoration={decoration} className="size-20" />
              ))
              .with(DecorationType.AvatarFrame, () => (
                <AvatarFrameItem decoration={decoration} />
              ))
              .with(DecorationType.ArtworkFrame, () => (
                <ArtworkFrameItem
                  decorationRelation={props.decorationRelation}
                ></ArtworkFrameItem>
              ))
              .with(DecorationType.LiveBanner, () => (
                <LiveBannerItemDisplay
                  type="preview"
                  className="size-full"
                  decoration={decoration}
                />
              ))
              .otherwise(() => null)}
          </Button>
        )}
      >
        <section className="p-3">
          {decoration.type === DecorationType.LiveBanner && (
            <LiveBannerItemDisplay
              type="video"
              decoration={decoration}
              className="mb-2 w-[300px]"
              mediaStreamPlayerStyleProps={{ className: 'max-h-[100px]' }}
            />
          )}
          <p className="text-center font-medium">
            {obtainedFromKey ? (
              <Text
                i18nKey={`growth:user-decorations.obtain-from.${obtainedFromKey}.label`}
                defaults={obtainedFromKey}
              />
            ) : (
              decorationName
            )}
          </p>
          {isMyProfile && (
            <p className="text-xs text-center text-balance text-neutral-500">
              {dayjs(obtainedAt).format('lll')}
            </p>
          )}
        </section>
      </UITooltipResponsive>

      {obtainedAt && (
        <>
          <div className="flex items-start gap-0.5">
            {decoration.type === DecorationType.LiveBanner && (
              <RiVideoAiLine className="size-4 text-pix-purple flex-shrink-0 mt-0.5" />
            )}
            <span className="text-sm text-center text-balance">
              {decorationName}
            </span>
          </div>

          {isMyProfile && (
            <ToggleDecorationButton
              decorationRelation={props.decorationRelation}
            />
          )}
          {isMyProfile && enableRemarkEdit && (
            <EditUserDecorationRemarkButton
              decorationRelation={props.decorationRelation}
            />
          )}
        </>
      )}
    </div>
  )
}

function AvatarFrameItem(props: { decoration: Decoration }) {
  const { decoration } = props
  const { user, isMyProfile } = useUserProfileContext()

  return isMyProfile ? (
    <UserAvatar
      user={user}
      decorations={{
        frame: decoration,
        badge: false,
      }}
      className="size-20"
    />
  ) : (
    <div className="size-20 relative">
      <AvatarFrame decoration={decoration} />
    </div>
  )
}

function ToggleDecorationButton(props: {
  decorationRelation: UserDecorationRelation
  onRefetch?: () => void
}) {
  const { decoration, isEnabled } = props.decorationRelation
  const [toggleDecoration] = useToggleActiveDecoration()

  const [state, onToggle] = useAsyncFn(
    async (target: boolean) => {
      await toggleDecoration({
        variables: { decorationId: decoration.id, enabled: target },
      })
      refetchGetMyInfo()
      props.onRefetch?.()
    },
    [toggleDecoration, props.onRefetch],
  )

  return (
    <UIButton
      variant={isEnabled ? 'filled' : 'outlined'}
      size="small"
      busy={state.loading}
      className="text-xs"
      onPress={() => onToggle(!isEnabled)}
    >
      {isEnabled ? (
        <span className="flex items-center">
          <MingcuteCheckFill className="size-4 -ml-0.5 mr-[0.25em]" />
          <Text
            i18nKey="user:user-decoration.use-button.label"
            context="used"
          />
        </span>
      ) : (
        <Text i18nKey="user:user-decoration.use-button.label" />
      )}
    </UIButton>
  )
}

function ArtworkFrameItem(props: {
  decorationRelation: UserDecorationRelation
  onRefetch?: () => void
}) {
  const { decorationRelation } = props
  const { decoration } = decorationRelation

  return (
    <div className="w-full aspect-square relative mb-1 p-2">
      <ArtworkFrame
        decoration={decoration}
        fillClassName="bg-white dark:bg-black"
      />
    </div>
  )
}
